import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

/*Import API helpers */
import { fetchGET } from "../apiHelper";
import {
  dashboardOverview,
  dashboardProjektiPoVlogi,
  dashboardProjektiPoClanici,
  dashboardProjektiPoProgramu,
  dashboardProjektiPoLetu,
  dashboardProjektiPoFinanciranju,
  dashboardZadnjiProjekti,
  dashboardPotekliProjekti,
} from "../listOfApi";

/*React-bootstrap components */
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import ProgressBar from "react-bootstrap/ProgressBar";
import Table from "react-bootstrap/Table";
import Badge from "react-bootstrap/Badge";

/*Bootstrap Icons */
import { 
  BsFolder, 
  BsCheckCircle, 
  BsCurrencyEuro,
  BsFileText,
  BsHourglass,
  BsXCircle,
  BsInfoCircle,
  BsArrowRightCircle,
} from "react-icons/bs";

const Dashboard = ({ role }) => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [projektiPoVlogi, setProjektiPoVlogi] = useState([]);
  const [projektiPoClanici, setProjektiPoClanici] = useState([]);
  const [projektiPoProgramu, setProjektiPoProgramu] = useState([]);
  const [projektiPoProgramuTotal, setProjektiPoProgramuTotal] = useState(0);
  const [projektiPoLetu, setProjektiPoLetu] = useState([]);
  const [projektiPoFinanciranju, setProjektiPoFinanciranju] = useState([]);
  const [zadnjiProjekti, setZadnjiProjekti] = useState([]);
  const [potekliProjekti, setPotekliProjekti] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const normalizeOverview = (data) => {
    if (!data || data === 0 || typeof data !== "object") {
      return null;
    }

    const getValue = (camelKey, pascalKey) => data[camelKey] ?? data[pascalKey];

    return {
      totalProjects: toNumber(getValue("totalProjects", "TotalProjects")),
      draftProjects: toNumber(getValue("draftProjects", "DraftProjects")),
      submittedProjects: toNumber(
        getValue("submittedProjects", "SubmittedProjects"),
      ),
      activeProjects: toNumber(getValue("activeProjects", "ActiveProjects")),
      closedProjects: toNumber(getValue("closedProjects", "ClosedProjects")),
      rejectedProjects: toNumber(
        getValue("rejectedProjects", "RejectedProjects"),
      ),
      totalNacrtovanoUM: toNumber(
        getValue("totalNacrtovanoUM", "TotalNacrtovanoUM"),
      ),
      totalRealiziranoUM: toNumber(
        getValue("totalRealiziranoUM", "TotalRealiziranoUM"),
      ),
      draftPercentage: toNumber(getValue("draftPercentage", "DraftPercentage")),
      submittedPercentage: toNumber(
        getValue("submittedPercentage", "SubmittedPercentage"),
      ),
      activePercentage: toNumber(getValue("activePercentage", "ActivePercentage")),
      closedPercentage: toNumber(
        getValue("closedPercentage", "ClosedPercentage"),
      ),
      rejectedPercentage: toNumber(
        getValue("rejectedPercentage", "RejectedPercentage"),
      ),
    };
  };

  const normalizeProgramData = (data) => {
    if (!data || data === 0) {
      return { items: [], total: 0 };
    }

    if (Array.isArray(data)) {
      const total = data.reduce(
        (sum, item) => sum + toNumber(item?.value ?? item?.Value),
        0,
      );
      return { items: data, total };
    }

    const items = Array.isArray(data.items)
      ? data.items
      : Array.isArray(data.Items)
        ? data.Items
        : [];
    const totalFromPayload = toNumber(data.total ?? data.Total);
    const totalFromItems = items.reduce(
      (sum, item) => sum + toNumber(item?.value ?? item?.Value),
      0,
    );

    return {
      items,
      total: totalFromPayload > 0 ? totalFromPayload : totalFromItems,
    };
  };

  useEffect(() => {
    const loadDashboardData = async () => {
      setIsLoading(true);

      try {
        const [
          overviewData,
          vlogiData,
          claniceData,
          programiData,
          letaData,
          financiranjeData,
          zadnjiData,
          potekliData
        ] = await Promise.all([
          fetchGET(dashboardOverview),
          fetchGET(dashboardProjektiPoVlogi),
          fetchGET(dashboardProjektiPoClanici),
          fetchGET(dashboardProjektiPoProgramu),
          fetchGET(dashboardProjektiPoLetu),
          fetchGET(dashboardProjektiPoFinanciranju),
          fetchGET(dashboardZadnjiProjekti),
          fetchGET(dashboardPotekliProjekti),
        ]);

        console.log("Dashboard API responses:", {
          overviewData,
          vlogiData,
          claniceData,
          programiData,
          letaData,
          financiranjeData,
          zadnjiData,
          potekliData,
        });

        if (overviewData && overviewData !== 0) {
          setOverview(normalizeOverview(overviewData));
        }
        if (vlogiData && vlogiData !== 0) setProjektiPoVlogi(Array.isArray(vlogiData) ? vlogiData : []);
        if (claniceData && claniceData !== 0) setProjektiPoClanici(Array.isArray(claniceData) ? claniceData : []);
        if (programiData && programiData !== 0) {
          const normalizedProgramiData = normalizeProgramData(programiData);
          setProjektiPoProgramu(normalizedProgramiData.items);
          setProjektiPoProgramuTotal(normalizedProgramiData.total);
        } else {
          setProjektiPoProgramu([]);
          setProjektiPoProgramuTotal(0);
        }
        if (letaData && letaData !== 0) setProjektiPoLetu(Array.isArray(letaData) ? letaData : []);
        if (financiranjeData && financiranjeData !== 0) setProjektiPoFinanciranju(Array.isArray(financiranjeData) ? financiranjeData : []);
        if (zadnjiData && zadnjiData !== 0) setZadnjiProjekti(Array.isArray(zadnjiData) ? zadnjiData : []);
        if (potekliData && potekliData !== 0) setPotekliProjekti(Array.isArray(potekliData) ? potekliData : []);
      } catch (error) {
        console.error("Error loading dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("sl-SI", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      useGrouping: true,
    }).format(value || 0);
  };

  const formatPercentage = (value) => {
    const safeValue = Number.isFinite(value) ? value : 0;
    return `${safeValue.toFixed(1)}%`;
  };

  const calculateStatusPercentage = (count) => {
    const total = overview?.totalProjects || 0;
    if (total <= 0) {
      return 0;
    }
    return (count / total) * 100;
  };

  const clampPercentage = (value) => {
    const safeValue = Number.isFinite(value) ? value : 0;
    if (safeValue < 0) {
      return 0;
    }
    if (safeValue > 100) {
      return 100;
    }
    return safeValue;
  };

  const draftProgress = clampPercentage(
    overview?.draftPercentage ?? calculateStatusPercentage(overview?.draftProjects || 0),
  );
  const activeProgress = clampPercentage(
    overview?.activePercentage ?? calculateStatusPercentage(overview?.activeProjects || 0),
  );
  const submittedProgress = clampPercentage(
    overview?.submittedPercentage ?? calculateStatusPercentage(overview?.submittedProjects || 0),
  );
  const closedProgress = clampPercentage(
    overview?.closedPercentage ?? calculateStatusPercentage(overview?.closedProjects || 0),
  );
  const rejectedProgress = clampPercentage(
    overview?.rejectedPercentage ?? calculateStatusPercentage(overview?.rejectedProjects || 0),
  );
  const unclosedColor = "#b67a6f";

  const getProjectId = (project) => {
    return (
      project?.id ??
      project?.ID ??
      project?.projektId ??
      project?.ProjektId ??
      project?.projektID ??
      project?.ProjektID
    );
  };

  const openProjectDetails = (project) => {
    const projectId = getProjectId(project);
    if (!projectId) {
      return;
    }
    navigate(`/Projekti/Podrobnosti/${projectId}`);
  };

  return (
    <Container fluid>
      <Row>
        <Col className="main-header font-weight-bold">Nadzorna plošča</Col>
      </Row>
      <Row>
        <Col className="header-divider">
          <hr />
        </Col>
      </Row>

      {isLoading && (
        <Row>
          <Col className="text-center py-5">
            <div>Nalaganje podatkov...</div>
          </Col>
        </Row>
      )}

      {!isLoading && overview && (
        <>
          {/* Top Row: Main Statistics */}
          <Row className="mb-4">
            <Col lg={3} md={6} className="mb-3">
              <Card className="main-container-card__shadows h-100" style={{ borderLeft: "4px solid #006a8e" }}>
                <Card.Body className="d-flex align-items-center">
                  <div className="me-3" style={{ fontSize: "2.5rem", color: "#006a8e" }}>
                    <BsFolder />
                  </div>
                  <div>
                    <h6 className="text-muted mb-1" style={{ fontSize: "0.875rem" }}>Skupno projektov</h6>
                    <h2 className="mb-0" style={{ fontWeight: "700" }}>{overview.totalProjects || 0}</h2>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={3} md={6} className="mb-3">
              <Card className="main-container-card__shadows h-100" style={{ borderLeft: "4px solid #006a8e" }}>
                <Card.Body className="d-flex align-items-center">
                  <div className="me-3" style={{ fontSize: "2.5rem", color: "#006a8e" }}>
                    <BsCheckCircle />
                  </div>
                  <div>
                    <h6 className="text-muted mb-1" style={{ fontSize: "0.875rem" }}>Aktivni projekti</h6>
                    <h2 className="mb-0" style={{ fontWeight: "700" }}>{overview.activeProjects || 0}</h2>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={3} md={6} className="mb-3">
              <Card className="main-container-card__shadows h-100" style={{ borderLeft: "4px solid #006a8e" }}>
                <Card.Body className="d-flex align-items-center">
                  <div className="me-3" style={{ fontSize: "2.5rem", color: "#006a8e" }}>
                    <BsCurrencyEuro />
                  </div>
                  <div>
                    <h6 className="text-muted mb-1" style={{ fontSize: "0.875rem" }}>Načrtovano (UM)</h6>
                    <h2 className="mb-0" style={{ fontWeight: "700", fontSize: "1.5rem" }}>{formatCurrency(overview.totalNacrtovanoUM)} €</h2>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Status Row: All Statuses */}
          <Row className="mb-4 row-cols-1 row-cols-md-2 row-cols-xl-5">
            <Col className="mb-3">
              <Card className="main-container-card__shadows h-100">
                <Card.Body>
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h6 className="text-muted mb-1" style={{ fontSize: "0.875rem" }}>Osnutki</h6>
                      <h3 className="mb-0" style={{ fontWeight: "600" }}>{overview.draftProjects || 0}</h3>
                      <small className="text-muted">{formatPercentage(draftProgress)}</small>
                    </div>
                    <BsFileText style={{ fontSize: "2rem", color: "#757575" }} />
                  </div>
                  <ProgressBar now={draftProgress} variant="secondary" style={{ height: "8px", marginTop: "10px" }} />
                </Card.Body>
              </Card>
            </Col>
            <Col className="mb-3">
              <Card className="main-container-card__shadows h-100">
                <Card.Body>
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h6 className="text-muted mb-1" style={{ fontSize: "0.875rem" }}>V izvajanju</h6>
                      <h3 className="mb-0" style={{ fontWeight: "600" }}>{overview.activeProjects || 0}</h3>
                      <small className="text-muted">{formatPercentage(activeProgress)}</small>
                    </div>
                    <BsCheckCircle style={{ fontSize: "2rem", color: "#757575" }} />
                  </div>
                  <ProgressBar now={activeProgress} variant="primary" style={{ height: "8px", marginTop: "10px" }} />
                </Card.Body>
              </Card>
            </Col>
            <Col className="mb-3">
              <Card className="main-container-card__shadows h-100">
                <Card.Body>
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h6 className="text-muted mb-1" style={{ fontSize: "0.875rem" }}>Prijavljeni</h6>
                      <h3 className="mb-0" style={{ fontWeight: "600" }}>{overview.submittedProjects || 0}</h3>
                      <small className="text-muted">{formatPercentage(submittedProgress)}</small>
                    </div>
                    <BsHourglass style={{ fontSize: "2rem", color: "#757575" }} />
                  </div>
                  <ProgressBar now={submittedProgress} variant="info" style={{ height: "8px", marginTop: "10px" }} />
                </Card.Body>
              </Card>
            </Col>
            <Col className="mb-3">
              <Card className="main-container-card__shadows h-100">
                <Card.Body>
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h6 className="text-muted mb-1" style={{ fontSize: "0.875rem" }}>Zaključeni</h6>
                      <h3 className="mb-0" style={{ fontWeight: "600" }}>{overview.closedProjects || 0}</h3>
                      <small className="text-muted">{formatPercentage(closedProgress)}</small>
                    </div>
                    <BsCheckCircle style={{ fontSize: "2rem", color: "#757575" }} />
                  </div>
                  <ProgressBar now={closedProgress} variant="dark" style={{ height: "8px", marginTop: "10px" }} />
                </Card.Body>
              </Card>
            </Col>
            <Col className="mb-3">
              <Card className="main-container-card__shadows h-100">
                <Card.Body>
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h6 className="text-muted mb-1" style={{ fontSize: "0.875rem" }}>Zavrnjeni</h6>
                      <h3 className="mb-0" style={{ fontWeight: "600" }}>{overview.rejectedProjects || 0}</h3>
                      <small className="text-muted">{formatPercentage(rejectedProgress)}</small>
                    </div>
                    <BsXCircle style={{ fontSize: "2rem", color: "#757575" }} />
                  </div>
                  <ProgressBar now={rejectedProgress} variant="secondary" style={{ height: "8px", marginTop: "10px" }} />
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Finance Row */}
          <Row className="mb-4">
            <Col md={6} className="mb-3">
              <Card className="main-container-card__shadows h-100" style={{ backgroundColor: "#f5f5f5" }}>
                <Card.Body>
                  <div className="d-flex align-items-center mb-2">
                    <BsCurrencyEuro style={{ fontSize: "1.5rem", color: "#006a8e", marginRight: "10px" }} />
                    <h5 className="mb-0" style={{ fontWeight: "600" }}>Načrtovano (UM)</h5>
                  </div>
                  <h2 style={{ fontWeight: "700", color: "#006a8e" }}>{formatCurrency(overview.totalNacrtovanoUM)} €</h2>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} className="mb-3">
              <Card className="main-container-card__shadows h-100" style={{ backgroundColor: "#f5f5f5" }}>
                <Card.Body>
                  <div className="d-flex align-items-center mb-2">
                    <BsCurrencyEuro style={{ fontSize: "1.5rem", color: "#525252", marginRight: "10px" }} />
                    <h5 className="mb-0" style={{ fontWeight: "600" }}>Realizirano (UM)</h5>
                  </div>
                  <h2 style={{ fontWeight: "700", color: "#525252" }}>{formatCurrency(overview.totalRealiziranoUM)} €</h2>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Recent Projects - moved up for priority */}
          {zadnjiProjekti.length > 0 && (
            <Row className="mb-4">
              <Col lg={12}>
                <Card className="main-container-card__shadows">
                  <Card.Header style={{ backgroundColor: "#f8f9fa", fontWeight: "600", fontSize: "1.1rem" }} className="d-flex justify-content-between align-items-center">
                    <span>Nedavni projekti</span>
                    <BsInfoCircle 
                      style={{ fontSize: "1rem", color: "#006a8e", cursor: "help" }} 
                      title="Zadnjih 10 projektov, ki so bili ustvarjeni ali posodobljeni. Kliknite na projekt za več podrobnosti."
                    />
                  </Card.Header>
                  <Card.Body className="p-0">
                    <Table hover responsive className="mb-0">
                      <thead style={{ backgroundColor: "#fafafa" }}>
                        <tr>
                          <th style={{ width: "40%" }}>Naziv</th>
                          <th style={{ width: "15%" }}>Akronim</th>
                          <th style={{ width: "20%" }}>Status</th>
                          <th style={{ width: "25%" }}>Zadnja sprememba</th>
                        </tr>
                      </thead>
                      <tbody>
                        {zadnjiProjekti.map((project, index) => {
                          // Status badge coloring
                          let badgeVariant = "secondary";
                          const status = (project.status || project.Status || "").toLowerCase();
                          
                          if (status.includes("aktiven") || status.includes("izvajanju")) {
                            badgeVariant = "primary";
                          } else if (status.includes("osnutek") || status.includes("draft")) {
                            badgeVariant = "secondary";
                          } else if (status.includes("prijavljen") || status.includes("submitted")) {
                            badgeVariant = "info";
                          } else if (status.includes("zaključen") || status.includes("closed")) {
                            badgeVariant = "dark";
                          } else if (status.includes("zavrnjen") || status.includes("rejected")) {
                            badgeVariant = "secondary";
                          }
                          
                          // Format timestamp
                          const formatTimestamp = (ts) => {
                            if (!ts) return "-";
                            try {
                              const date = new Date(ts);
                              return date.toLocaleDateString("sl-SI", {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit"
                              });
                            } catch {
                              return ts;
                            }
                          };
                          
                          return (
                            <tr key={project.id || project.ID || index} style={{ cursor: "pointer" }}>
                              <td style={{ fontWeight: "500" }}>{project.naziv || project.Naziv || "-"}</td>
                              <td>
                                <code style={{ backgroundColor: "#f5f5f5", padding: "2px 6px", borderRadius: "3px", fontSize: "0.85rem" }}>
                                  {project.akronim || project.Akronim || "-"}
                                </code>
                              </td>
                              <td>
                                <Badge bg={badgeVariant}>
                                  {project.status || project.Status || "-"}
                                </Badge>
                              </td>
                              <td style={{ fontSize: "0.9rem", color: "#666" }}>
                                {formatTimestamp(project.timestamp || project.Timestamp)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Projects by Role and Faculty */}
          <Row className="mb-4">
            {/* Projekti po vlogi */}
            {projektiPoVlogi.length > 0 && (
              <Col lg={6} className="mb-3">
                <Card className="main-container-card__shadows h-100">
                  <Card.Header style={{ backgroundColor: "#f8f9fa", fontWeight: "600", fontSize: "1.1rem" }} className="d-flex justify-content-between align-items-center">
                    <span>Projekti po vlogi UM</span>
                    <BsInfoCircle 
                      style={{ fontSize: "1rem", color: "#006a8e", cursor: "help" }} 
                      title="Razdelitev projektov glede na vlogo Univerze v Mariboru: Koordinator, Partner ali Prijavitelj."
                    />
                  </Card.Header>
                  <Card.Body>
                    {projektiPoVlogi.map((item, index) => {
                      const total = projektiPoVlogi.reduce((sum, p) => sum + (p.value || p.Value || 0), 0);
                      const percentage = total > 0 ? Math.round(((item.value || item.Value || 0) / total) * 100) : 0;
                      
                      // Color coding by role
                      let variant = "primary";
                      const label = (item.label || item.Label || "").toLowerCase();
                      
                      if (label.includes("koordinator")) {
                        variant = "primary";
                      } else if (label.includes("partner")) {
                        variant = "secondary";
                      } else if (label.includes("prijavitelj")) {
                        variant = "info";
                      }
                      
                      return (
                        <div key={index} className="mb-3">
                          <div className="d-flex justify-content-between mb-2">
                            <span style={{ fontWeight: "500" }}>{item.label || item.Label}</span>
                            <span>
                              <strong>{item.value || item.Value}</strong> <span className="text-muted">({percentage}%)</span>
                            </span>
                          </div>
                          <ProgressBar
                            variant={variant}
                            now={percentage}
                            style={{ height: "25px" }}
                            label={`${percentage}%`}
                          />
                        </div>
                      );
                    })}
                  </Card.Body>
                </Card>
              </Col>
            )}

            {/* Projekti po članici */}
            {projektiPoClanici.length > 0 && (
              <Col lg={6} className="mb-3">
                <Card className="main-container-card__shadows h-100">
                  <Card.Header style={{ backgroundColor: "#f8f9fa", fontWeight: "600", fontSize: "1.1rem" }} className="d-flex justify-content-between align-items-center">
                    <span>Projekti po članici (Top 10)</span>
                    <BsInfoCircle 
                      style={{ fontSize: "1rem", color: "#006a8e", cursor: "help" }} 
                      title="10 članic Univerze v Mariboru z največ projekti. Prikazuje število projektov za vsako članico."
                    />
                  </Card.Header>
                  <Card.Body style={{ maxHeight: "400px", overflowY: "auto" }}>
                    {projektiPoClanici.slice(0, 10).map((item, index) => {
                      const maxValue = Math.max(...projektiPoClanici.map(p => p.value || p.Value || 0));
                      const percentage = maxValue > 0 ? Math.round(((item.value || item.Value || 0) / maxValue) * 100) : 0;
                      
                      // Generate color gradient based on position
                      const colors = ["#006a8e", "#4a8fa3", "#6fa9b8", "#94c3cd", "#b9dde2"];
                      const colorIndex = Math.min(index % colors.length, colors.length - 1);
                      
                      return (
                        <div key={index} className="mb-3">
                          <div className="d-flex justify-content-between mb-1">
                            <small style={{ fontWeight: "500" }}>{item.label || item.Label}</small>
                            <span>
                              <strong>{item.value || item.Value}</strong>
                            </span>
                          </div>
                          <ProgressBar
                            now={percentage}
                            style={{ height: "20px", backgroundColor: "#e0e0e0" }}
                          >
                            <div
                              style={{
                                width: `${percentage}%`,
                                backgroundColor: colors[colorIndex],
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                fontWeight: "600",
                                fontSize: "0.875rem"
                              }}
                            >
                              {percentage}%
                            </div>
                          </ProgressBar>
                        </div>
                      );
                    })}
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>

          {/* Projects by Program and by Year */}
          <Row className="mb-4">
            {/* Projekti po programu */}
            {projektiPoProgramu.length > 0 && (
              <Col lg={6} className="mb-3">
                <Card className="main-container-card__shadows h-100">
                  <Card.Header style={{ backgroundColor: "#f8f9fa", fontWeight: "600", fontSize: "1.1rem" }} className="d-flex justify-content-between align-items-center">
                    <span>Projekti po programu</span>
                    <BsInfoCircle 
                      style={{ fontSize: "1rem", color: "#006a8e", cursor: "help" }} 
                      title="Porazdelitev projektov po glavnih programih financiranja (npr. Horizon Europe, ARRS, Erasmus+)."
                    />
                  </Card.Header>
                  <Card.Body>
                    {projektiPoProgramu.slice(0, 10).map((item, index) => {
                      const value = Number(item.value || item.Value || 0);
                      const totalPrograms =
                        projektiPoProgramuTotal > 0
                          ? projektiPoProgramuTotal
                          : projektiPoProgramu.reduce(
                              (sum, programItem) =>
                                sum + Number(programItem.value || programItem.Value || 0),
                              0,
                            );
                      const percentage =
                        totalPrograms > 0 ? (value / totalPrograms) * 100 : 0;
                      
                      // Color palette for programs
                      const colors = ["#006a8e", "#525252", "#757575", "#4a8fa3", "#616161", "#6fa9b8", "#424242", "#78909C"];
                      const color = colors[index % colors.length];
                      
                      return (
                        <div key={index} className="mb-3">
                          <div className="d-flex justify-content-between mb-1">
                            <span style={{ fontWeight: "500", fontSize: "0.9rem" }}>{item.label || item.Label}</span>
                            <span>
                              <strong style={{ fontSize: "1.1rem" }}>{value}</strong> <span className="text-muted">({Math.round(percentage)}%)</span>
                            </span>
                          </div>
                          <div style={{ position: "relative", height: "24px", backgroundColor: "#e0e0e0", borderRadius: "4px", overflow: "hidden" }}>
                            <div
                              style={{
                                width: `${percentage}%`,
                                backgroundColor: color,
                                height: "100%",
                                transition: "width 0.3s ease",
                                display: "flex",
                                alignItems: "center",
                                paddingLeft: "8px",
                                color: "white",
                                fontWeight: "600",
                                fontSize: "0.875rem"
                              }}
                            >
                              {percentage > 15 ? `${Math.round(percentage)}%` : ''}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </Card.Body>
                </Card>
              </Col>
            )}

            {/* Projekti po letu */}
            {projektiPoLetu.length > 0 && (
              <Col lg={6} className="mb-3">
                <Card className="main-container-card__shadows h-100">
                  <Card.Header style={{ backgroundColor: "#f8f9fa", fontWeight: "600", fontSize: "1.1rem" }} className="d-flex justify-content-between align-items-center">
                    <span>Projekti po letih (5 let nazaj / naprej)</span>
                    <BsInfoCircle 
                      style={{ fontSize: "1rem", color: "#006a8e", cursor: "help" }} 
                      title="Prikaz zaključenih in nezaključenih projektov po letih; nezaključeni so označeni z umirjeno barvo."
                    />
                  </Card.Header>
                  <Card.Body>
                    <div className="d-flex gap-3 mb-3" style={{ fontSize: "0.85rem" }}>
                      <span className="d-flex align-items-center gap-1">
                        <span style={{ width: "10px", height: "10px", borderRadius: "2px", backgroundColor: "#006a8e", display: "inline-block" }}></span>
                        Zaključeni
                      </span>
                      <span className="d-flex align-items-center gap-1" style={{ color: unclosedColor }}>
                        <span style={{ width: "10px", height: "10px", borderRadius: "2px", backgroundColor: unclosedColor, display: "inline-block" }}></span>
                        Nezaključeni
                      </span>
                    
                    </div>
                    <div style={{ position: "relative", height: "320px" }}>
                      <div style={{ display: "flex", height: "100%", alignItems: "flex-end", justifyContent: "space-around", borderBottom: "2px solid #333", paddingBottom: "5px" }}>
                        {(() => {
                          const currentYear = new Date().getFullYear();
                          const sortedData = [...projektiPoLetu]
                            .filter((item) => {
                              const year = Number(item.year ?? item.Year ?? 0);
                              return year >= currentYear - 5 && year <= currentYear + 5;
                            })
                            .sort((a, b) => {
                              const firstYear = Number(a.year ?? a.Year ?? 0);
                              const secondYear = Number(b.year ?? b.Year ?? 0);
                              return firstYear - secondYear;
                            });

                          const maxCount = Math.max(
                            ...sortedData.map((item) => {
                              const zakljuceni = Number(item.zakljuceni ?? item.Zakljuceni ?? 0);
                              const nezakljuceni = Number(item.nezakljuceni ?? item.Nezakljuceni ?? 0);
                              return Math.max(zakljuceni + nezakljuceni, 0);
                            }),
                            1,
                          );

                          return sortedData.map((item, index) => {
                            const year = item.year ?? item.Year;
                            const zakljuceni = Number(item.zakljuceni ?? item.Zakljuceni ?? 0);
                            const nezakljuceni = Number(item.nezakljuceni ?? item.Nezakljuceni ?? 0);
                            const total = Math.max(zakljuceni + nezakljuceni, 0);
                            const totalHeight = (total / maxCount) * 100;
                            const closedRatio = total > 0 ? (zakljuceni / total) * 100 : 0;
                            const unclosedRatio = total > 0 ? (nezakljuceni / total) * 100 : 0;

                            return (
                              <div key={year || index} style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1, margin: "0 5px" }}>
                                <div style={{ width: "100%", display: "flex", alignItems: "flex-end", justifyContent: "center", height: "240px" }}>
                                  <div
                                    style={{
                                      width: "52%",
                                      height: `${totalHeight}%`,
                                      backgroundColor: "#f1f1f1",
                                      borderRadius: "4px 4px 0 0",
                                      position: "relative",
                                      minHeight: total > 0 ? "6px" : "0",
                                      overflow: "hidden",
                                    }}
                                    title={`Ostali: ${zakljuceni}, Nezaključeni: ${nezakljuceni}, Skupaj: ${total}`}
                                  >
                                    <div
                                      style={{
                                        position: "absolute",
                                        bottom: 0,
                                        left: 0,
                                        width: "100%",
                                        height: `${closedRatio}%`,
                                        backgroundColor: "#006a8e",
                                      }}
                                    ></div>
                                    <div
                                      style={{
                                        position: "absolute",
                                        bottom: `${closedRatio}%`,
                                        left: 0,
                                        width: "100%",
                                        height: `${unclosedRatio}%`,
                                        backgroundColor: unclosedColor,
                                      }}
                                    ></div>
                                    <div style={{ position: "absolute", top: "-18px", left: "50%", transform: "translateX(-50%)", fontWeight: "700", fontSize: "0.8rem", color: "#006a8e" }}>
                                      {total}
                                    </div>
                                  </div>
                                </div>
                                <div style={{ marginTop: "8px", fontWeight: "600", fontSize: "0.9rem", color: "#555" }}>
                                  {year}
                                </div>
                                <div style={{ fontSize: "0.78rem", fontWeight: "600", display: "flex", gap: "4px", justifyContent: "center" }}>
                                  <span style={{ color: "#006a8e" }}>{zakljuceni}</span>
                                  <span>/</span>
                                  <span style={{ color: unclosedColor }}>{nezakljuceni}</span>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>

          {/* Potekli projekti */}
          {potekliProjekti.length > 0 && (
            <Row className="mb-4">
              <Col lg={12}>
                <Card className="main-container-card__shadows">
                  <Card.Header style={{ backgroundColor: "#f8f9fa", fontWeight: "600", fontSize: "1.1rem" }} className="d-flex justify-content-between align-items-center">
                    <span>Potekli projekti</span>
                    <BsInfoCircle
                      style={{ fontSize: "1rem", color: "#006a8e", cursor: "help" }}
                      title="Projekti, ki so presegli datum konca, a niso formalno zaključeni."
                    />
                  </Card.Header>
                  <Card.Body className="p-0">
                    <Table hover responsive className="mb-0">
                      <thead style={{ backgroundColor: "#fafafa" }}>
                        <tr>
                          <th style={{ width: "35%" }}>Naziv</th>
                          <th style={{ width: "12%" }}>Akronim</th>
                          <th style={{ width: "20%" }}>Status</th>
                          <th style={{ width: "16%" }}>Začetek</th>
                          <th style={{ width: "17%" }}>Konec</th>
                          <th style={{ width: "1%" }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {potekliProjekti.map((project, index) => {
                          const formatDate = (dateStr) => {
                            if (!dateStr) return "-";
                            try {
                              return new Date(dateStr).toLocaleDateString("sl-SI", {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                              });
                            } catch {
                              return dateStr;
                            }
                          };

                          let badgeVariant = "secondary";
                          const statusText = project.status || project.Status || "-";
                          const status = String(statusText).toLowerCase();
                          if (status.includes("izvajanju") || status.includes("aktiven")) {
                            badgeVariant = "primary";
                          } else if (status.includes("prijavljen")) {
                            badgeVariant = "info";
                          } else if (status.includes("zaključen")) {
                            badgeVariant = "dark";
                          }

                          const projectId = getProjectId(project);
                          const isClickable = Boolean(projectId);

                          return (
                            <tr key={projectId || index}>
                              <td style={{ fontWeight: "500" }}>{project.naziv || project.Naziv || "-"}</td>
                              <td>
                                <code style={{ backgroundColor: "#f5f5f5", padding: "2px 6px", borderRadius: "3px", fontSize: "0.85rem" }}>
                                  {project.akronim || project.Akronim || "-"}
                                </code>
                              </td>
                              <td>
                                <Badge bg={badgeVariant}>{statusText}</Badge>
                              </td>
                              <td style={{ fontSize: "0.9rem", color: "#666" }}>{formatDate(project.zacetek || project.Zacetek)}</td>
                              <td style={{ fontSize: "0.9rem", color: "#c0392b", fontWeight: "500" }}>{formatDate(project.konec || project.Konec)}</td>
                              <td className="text-end">
                                <button
                                  type="button"
                                  onClick={() => openProjectDetails(project)}
                                  disabled={!isClickable}
                                  aria-label="Odpri podrobnosti projekta"
                                  title={isClickable ? "Odpri podrobnosti projekta" : "ID projekta ni na voljo"}
                                  style={{
                                    border: "none",
                                    background: "transparent",
                                    padding: 0,
                                    color: isClickable ? "#006a8e" : "#b0b0b0",
                                    cursor: isClickable ? "pointer" : "not-allowed",
                                  }}
                                >
                                  <BsArrowRightCircle size={18} />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Funding Type and Citizen Science */}
          <Row className="mb-4">
            {/* Projekti po financiranju */}
            {projektiPoFinanciranju.length > 0 && (
              <Col lg={6} className="mb-3">
                <Card className="main-container-card__shadows h-100">
                  <Card.Header style={{ backgroundColor: "#f8f9fa", fontWeight: "600", fontSize: "1.1rem" }} className="d-flex justify-content-between align-items-center">
                    <span>Projekti po tipu financiranja</span>
                    <BsInfoCircle 
                      style={{ fontSize: "1rem", color: "#006a8e", cursor: "help" }} 
                      title="Razdelitev projektov glede na vrsto financiranja: javni razpisi, strukturni skladi, tržni projekti itd."
                    />
                  </Card.Header>
                  <Card.Body>
                    {projektiPoFinanciranju.map((item, index) => {
                      const total = projektiPoFinanciranju.reduce((sum, p) => sum + (p.value || p.Value || 0), 0);
                      const percentage = total > 0 ? Math.round(((item.value || item.Value || 0) / total) * 100) : 0;
                      
                      // Color coding by funding type
                      const colors = ["#006a8e", "#525252", "#757575", "#616161", "#424242"];
                      const color = colors[index % colors.length];
                      
                      return (
                        <div key={index} className="mb-3">
                          <div className="d-flex justify-content-between mb-2">
                            <span style={{ fontWeight: "500" }}>{item.label || item.Label}</span>
                            <span>
                              <strong>{item.value || item.Value}</strong> <span className="text-muted">({percentage}%)</span>
                            </span>
                          </div>
                          <ProgressBar
                            now={percentage}
                            style={{ height: "30px", backgroundColor: "#e0e0e0" }}
                          >
                            <div
                              style={{
                                width: `${percentage}%`,
                                backgroundColor: color,
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                fontWeight: "700",
                                fontSize: "1rem"
                              }}
                            >
                              {percentage}%
                            </div>
                          </ProgressBar>
                        </div>
                      );
                    })}
                  </Card.Body>
                </Card>
              </Col>
            )}

          </Row>
        </>
      )}
    </Container>
  );
};

export default Dashboard;
